import { AutoCustomCompleteField, Card, CardContent, FormField, FormGrid, FormShell, MessageAlert, RBButton, VirtualTree, Timeline, CustomDateControl } from '@scmlatam/web-ui';
import { Icon } from '@mui/material';
import dayjs from 'dayjs';
import type { useFormAssignPosition } from '../hooks/useFormAssignPosition';
import WorkerAssignPositionIcon from '../../../../assets/icons/worker-assign-position.svg';

type FormProps = ReturnType<typeof useFormAssignPosition>;

const formatDateLabel = (value: string) => dayjs(value).format('DD/MM/YYYY');

const HeaderAlert = ({ formProps }: { formProps: FormProps }) => {
    if (!formProps.showMessage) return null;

    return (
        <MessageAlert
            type="warning"
            title={formProps.t('assign_position.alerts.unsaved_changes.title')}
            message={formProps.t('assign_position.alerts.unsaved_changes.message')}
            className="rb-message-alert--form"
            actions={[
                {
                    label: formProps.t('assign_position.alerts.unsaved_changes.action_button'),
                    onClick: () => formProps.setShowMessage(false),
                },
                {
                    label: formProps.t('assign_position.alerts.unsaved_changes.cancel_button'),
                    onClick: () => {
                        formProps.setShowMessage(false);
                        formProps.closeForm(true);
                    },
                },
            ]}
        />
    );
};

const Footer = ({ formProps }: { formProps: FormProps }) => (
    <div className="flex flex-wrap items-center justify-between gap-3 p-3">
        <RBButton variant="secondary" size="m" disabled={formProps.saving} onClick={() => formProps.closeForm(false)}>
            {formProps.t('assign_position.buttons.cancel')}
        </RBButton>
        <RBButton size="s" loading={formProps.saving} disabled={!formProps.isValid || formProps.saving || formProps.loading} onClick={formProps.save}>
            {formProps.t('assign_position.buttons.add')}
        </RBButton>
    </div>
);

const PositionTreeDialog = ({ formProps }: { formProps: FormProps }) => (
    <FormShell
        open={formProps.openTree}
        title={formProps.t('assign_position.tree.title')}
        size="m"
        onClose={() => formProps.setOpenTree(false)}
        footer={
            <div className="flex justify-end gap-3 p-3">
                <RBButton variant="secondary" size="m" onClick={() => formProps.setOpenTree(false)}>
                    {formProps.t('assign_position.buttons.cancel')}
                </RBButton>
                <RBButton size="m" disabled={!formProps.selectedPosition} onClick={formProps.addPosition}>
                    {formProps.t('assign_position.buttons.assign')}
                </RBButton>
            </div>
        }
    >
        <VirtualTree
            items={formProps.positionTreeItems}
            selectionMode="single"
            selectedIds={formProps.selectedPosition ? [formProps.selectedPosition.nodeId] : []}
            onSelectionChange={formProps.handlePositionSelectionChange}
            defaultExpandAll
            searchLabel={formProps.t('assign_position.tree.search')}
            searchPlaceholder={formProps.t('assign_position.tree.search_placeholder')}
            clearSearchLabel={formProps.t('assign_position.tree.clear_search')}
            noResultsText={formProps.t('assign_position.tree.no_results')}
            emptyText={formProps.t('assign_position.tree.empty')}
            loading={formProps.loading}
        />
    </FormShell>
);

export const AssignPositionDialog = ({ formProps }: { formProps: FormProps }) => (
    <>
        <FormShell
            open={formProps.openForm}
            title={formProps.title}
            size="l"
            className="max-w-[min(1180px,calc(100vw-2rem))] [&>div>div.relative]:!mt-4"
            footerClassName="!mt-4"
            onClose={() => formProps.closeForm(false)}
            loading={formProps.loading}
            alert={<HeaderAlert formProps={formProps} />}
            footer={<Footer formProps={formProps} />}
        >
            <FormGrid>
                <FormField span={12}>{formProps.employeeLabel && <p className="text-base font-semibold text-[#26000B]">{formProps.employeeLabel}</p>}</FormField>
                <FormField span={12}>
                    <AutoCustomCompleteField
                        selectionMode="single"
                        label={formProps.t('assign_position.fields.contract')}
                        placeholder={formProps.t('assign_position.placeholder.select')}
                        values={formProps.hiringOptions ?? []}
                        value={String(formProps.selectedHiringId ?? '')}
                        setValue={(hiringId) => formProps.setSelectedHiringId(Number(hiringId) || null)}
                        white
                        disabled={formProps.loading}
                    />
                </FormField>
                {formProps.selectedHiringRange && formProps.timelineSegments.length > 0 && (
                    <FormField span={12}>
                        <Timeline
                            className="px-3"
                            mode="compact"
                            segments={formProps.timelineSegments}
                            external_labels={{
                                fecha_inicio: formatDateLabel(formProps.selectedHiringRange.start_date),
                                fecha_fin: formatDateLabel(formProps.selectedHiringRange.end_date),
                            }}
                            allowGaps
                            tooltips
                            scale="proportional"
                        />
                    </FormField>
                )}
                <FormField span={12}>
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-[#26000B]">{formProps.t('assign_position.fields.selected_positions')}</h3>
                            {formProps.activePositionsCount > 0 && (
                                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-800">{formProps.activePositionsCount}</span>
                            )}
                        </div>
                        <RBButton
                            variant="secondary"
                            size="s"
                            disabled={!formProps.selectedHiringId || formProps.loading || formProps.saving}
                            onClick={() => {
                                formProps.setTreeRowId(null);
                                formProps.setSelectedPosition(null);
                                formProps.setOpenTree(true);
                            }}
                        >
                            {formProps.t('assign_position.buttons.add_position')}
                        </RBButton>
                    </div>
                    {/* {formProps.coverageError && (
                        <p className="mt-1 text-right text-xs font-medium" style={{ color: '#dc2626' }}>
                            {formProps.coverageError}
                        </p>
                    )} */}
                </FormField>
                <FormField span={12}>
                    <div className="flex min-h-40 flex-col gap-3">
                        {!formProps.selectedHiringId && (
                            <div className="flex flex-col items-center gap-2 py-8">
                                <img src={WorkerAssignPositionIcon} alt="worker-assign-position-icon" />
                                <p className="text-center text-[#625d5d]">{formProps.t('assign_position.alerts.unselected_hiring.message')}</p>
                            </div>
                        )}
                        {formProps.selectedHiringId && !formProps.selectedHiringRange && (
                            <Card variant="default" className="flex justify-center">
                                <CardContent className="flex flex-col items-center gap-2">
                                    <img src={WorkerAssignPositionIcon} alt="worker-assign-position-icon" />
                                    {formProps.t('assign_position.alerts.empty_position.message')}
                                </CardContent>
                            </Card>
                        )}
                        {formProps.selectedHiringRange && formProps.positionRows.length === 0 && (
                            <div className="flex flex-col items-center gap-2 py-8">
                                <img src={WorkerAssignPositionIcon} alt="worker-assign-position-icon" />
                                <p className="text-center text-[#625d5d]">{formProps.t('assign_position.alerts.empty_position.message')}</p>
                            </div>
                        )}
                        {formProps.positionRows.map((row, index) => {
                            if (row.toDelete) {
                                return (
                                    <Card key={row.localId} variant="default" className="border border-gray-200 bg-white">
                                        <CardContent className="flex items-center justify-between p-4">
                                            <p className="font-semibold text-[#26000B]">Puesto eliminado</p>
                                            <RBButton
                                                variant="ghost"
                                                size="s"
                                                className="font-bold text-[#26000B] hover:bg-gray-100"
                                                disabled={formProps.saving}
                                                onClick={() => formProps.restorePosition(row.localId)}
                                            >
                                                {formProps.t('assign_position.buttons.restore') || 'Deshacer'}
                                            </RBButton>
                                        </CardContent>
                                    </Card>
                                );
                            }

                            return (
                                <Card key={row.localId} variant="default" className="relative border border-gray-200 bg-white">
                                    <CardContent className="flex flex-col gap-2.5 p-4">
                                        {/* Insignia / Badge */}
                                        <div>
                                            <span className="inline-block rounded-full bg-gray-100 px-3 py-0.5 text-xs font-semibold text-gray-700">Asignación {index + 1}</span>
                                        </div>

                                        {/* Título de Puesto */}
                                        <h4 className="text-lg font-bold text-[#26000B]">{row.workPositionName}</h4>

                                        {/* Ruta Orgpath con botón lápiz */}
                                        <div className="flex items-center gap-3 text-sm text-[#625d5d]">
                                            <span>
                                                {row.subcompanyName}
                                                {row.areaNames.length ? `  >  ${row.areaNames.join('  >  ')}` : ''}
                                            </span>
                                            <RBButton
                                                variant="ghost"
                                                size="s"
                                                className="!p-1 text-gray-500 hover:text-black"
                                                disabled={formProps.loading || formProps.saving}
                                                onClick={() => formProps.editPosition(row.localId)}
                                            >
                                                <Icon baseClassName="material-symbols-rounded" style={{ fontSize: '18px' }}>
                                                    edit
                                                </Icon>
                                            </RBButton>
                                        </div>

                                        {/* Fechas: Modo Lectura vs Modo Edición */}
                                        <div className="flex items-center justify-between gap-3 pt-1">
                                            {!row.editingDates ? (
                                                <div className="flex items-center gap-2 text-sm font-medium text-[#26000B]">
                                                    <span>
                                                        {row.startDate ? row.startDate.format('DD/MM/YYYY') : 'DD/MM/YYYY'} → {row.endDate ? row.endDate.format('DD/MM/YYYY') : 'DD/MM/YYYY'}
                                                    </span>
                                                    <RBButton
                                                        variant="ghost"
                                                        size="s"
                                                        className="!p-1 text-gray-500 hover:text-black"
                                                        disabled={formProps.loading || formProps.saving}
                                                        onClick={() => formProps.toggleEditDates(row.localId)}
                                                    >
                                                        <Icon baseClassName="material-symbols-rounded" style={{ fontSize: '18px' }}>
                                                            edit
                                                        </Icon>
                                                    </RBButton>
                                                </div>
                                            ) : (
                                                <div className="flex flex-wrap sm:flex-nowrap items-end gap-2.5 py-1">
                                                    <div className="w-36 sm:w-44">
                                                        <CustomDateControl
                                                            label={formProps.t('assign_position.fields.start_date')}
                                                            value={row.startDate}
                                                            setValue={(value) => formProps.updateDate(row.localId, 'startDate', value?.format('YYYY-MM-DD') ?? '')}
                                                            placeholder={formProps.t('assign_position.placeholder.select')}
                                                            white
                                                            disabled={formProps.loading || formProps.saving}
                                                            variant="field"
                                                            minDate={formProps.selectedHiringRange ? dayjs(formProps.selectedHiringRange.start_date) : undefined}
                                                            maxDate={formProps.selectedHiringRange ? dayjs(formProps.selectedHiringRange.end_date) : undefined}
                                                        />
                                                    </div>
                                                    <span className="px-2 pb-3 text-sm font-medium text-gray-500">a</span>
                                                    <div className="w-36 sm:w-44">
                                                        <CustomDateControl
                                                            label={formProps.t('assign_position.fields.end_date')}
                                                            value={row.endDate}
                                                            setValue={(value) => formProps.updateDate(row.localId, 'endDate', value?.format('YYYY-MM-DD') ?? '')}
                                                            placeholder={formProps.t('assign_position.placeholder.select')}
                                                            white
                                                            disabled={formProps.loading || formProps.saving}
                                                            variant="field"
                                                            minDate={formProps.selectedHiringRange ? dayjs(formProps.selectedHiringRange.start_date) : undefined}
                                                            maxDate={formProps.selectedHiringRange ? dayjs(formProps.selectedHiringRange.end_date) : undefined}
                                                        />
                                                    </div>
                                                    <RBButton
                                                        variant="ghost"
                                                        size="s"
                                                        className="mb-1 !p-1.5 text-purple-700 hover:bg-purple-50"
                                                        onClick={() => formProps.toggleEditDates(row.localId)}
                                                    >
                                                        <Icon baseClassName="material-symbols-rounded" style={{ fontSize: '20px' }}>
                                                            check
                                                        </Icon>
                                                    </RBButton>
                                                </div>
                                            )}

                                            {/* Botón de eliminar (Papelera roja) en la esquina inferior derecha */}
                                            <RBButton
                                                variant="ghost"
                                                size="s"
                                                className="!p-1 text-red-600 hover:bg-red-50 hover:text-red-800"
                                                disabled={formProps.loading || formProps.saving}
                                                onClick={() => formProps.removePosition(row.localId)}
                                            >
                                                <Icon baseClassName="material-symbols-rounded" style={{ fontSize: '20px' }}>
                                                    delete
                                                </Icon>
                                            </RBButton>
                                        </div>

                                        {row.errors.length > 0 && (
                                            <div className="flex flex-col gap-0.5">
                                                {row.errors.map((err, errIdx) => (
                                                    <span key={errIdx} className="text-sm font-medium" style={{ color: '#dc2626' }}>
                                                        {err}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </FormField>
            </FormGrid>
        </FormShell>
        <PositionTreeDialog formProps={formProps} />
    </>
);
